/**
 * MLAnalyzer - Modulo per l'analisi predittiva basata su machine learning
 * Analizza lo storico dell'utente per prevedere recupero, rischi di plateau e progresso
 */

// Utility per la normalizzazione e preparazione dei dati
import { preprocessTrainingData, extractFeatures } from '../utils/MachineLearningUtils';

/**
 * Classe principale per l'analisi ML dei dati di allenamento
 */
export class MLAnalyzer {
  /**
   * Inizializza l'analizzatore ML
   * @param {Object} historicalData - Dati storici dell'utente
   * @param {Array} historicalData.workouts - Allenamenti storici
   * @param {Array} historicalData.sets - Serie storiche
   * @param {Object} userProfile - Profilo dell'utente (opzionale)
   */
  constructor(historicalData, userProfile = {}) {
    this.userData = historicalData;
    this.userProfile = userProfile;
    this.trainedModels = {};
    this.workoutPatterns = [];
    this.userMetrics = {
      recoverySpeed: null,      // Velocità di recupero stimata (giorni)
      volumeTolerance: null,    // Tolleranza al volume (1-10)
      intensityTolerance: null, // Tolleranza all'intensità (1-10)
      progressionRate: null,    // Tasso di progressione medio (% settimana)
      plateauFrequency: null    // Frequenza media dei plateau (settimane)
    };
  }

  /**
   * Inizializza e prepara i dati per l'analisi
   * @returns {MLAnalyzer} Istanza inizializzata
   */
  async initialize() {
    if (!this.hasMinimumData()) {
      console.warn('Dati insufficienti per un\'analisi ML accurata');
      return this;
    }

    try {
      // Prepara e normalizza i dati
      const processedData = this.preprocessData();
      
      // Analizza i pattern di allenamento
      this.workoutPatterns = this.analyzeWorkoutPatterns();
      
      // Calcola metriche utente
      this.calculateUserMetrics();
      
      // Addestra modelli di base se ci sono dati sufficienti
      if (this.userData.workouts.length >= 20) {
        await this.trainBaseModels(processedData);
      }
    } catch (error) {
      console.error('Errore durante l\'inizializzazione ML:', error);
    }
    
    return this;
  }

  /**
   * Verifica se ci sono dati sufficienti per l'analisi
   * @returns {boolean} True se ci sono dati sufficienti
   */
  hasMinimumData() {
    return (
      this.userData && 
      this.userData.workouts && 
      this.userData.workouts.length >= 9 &&
      this.userData.sets && 
      this.userData.sets.length >= 50
    );
  }

  /**
   * Preprocessa i dati per l'analisi ML
   * @returns {Object} Dati processati e features estratte
   */
  preprocessData() {
    try {
      // Utilizza utility di preprocessing
      return preprocessTrainingData(this.userData);
    } catch (error) {
      console.error('Errore nel preprocessing dei dati:', error);
      return {
        workoutFeatures: [],
        exerciseFeatures: [],
        timeSeriesData: []
      };
    }
  }

  /**
   * Analizza i pattern di allenamento dell'utente
   * @returns {Array} Pattern identificati
   */
  analyzeWorkoutPatterns() {
    const patterns = [];
    const { workouts } = this.userData;
    
    if (workouts.length < 10) return patterns;
    
    try {
      // Identifica split di allenamento (es. push/pull/legs, upper/lower)
      const splitPattern = this.detectTrainingSplit(workouts);
      if (splitPattern) {
        patterns.push(splitPattern);
      }
      
      // Identifica pattern di volume (lineari, ondulati, etc)
      const volumePattern = this.detectVolumePattern(workouts);
      if (volumePattern) {
        patterns.push(volumePattern);
      }
      
      // Identifica pattern di frequenza
      const frequencyPattern = this.detectFrequencyPattern(workouts);
      if (frequencyPattern) {
        patterns.push(frequencyPattern);
      }
    } catch (error) {
      console.error('Errore nell\'analisi dei pattern:', error);
    }
    
    return patterns;
  }

  /**
   * Rileva il tipo di split di allenamento utilizzato
   * @param {Array} workouts - Allenamenti storici
   * @returns {Object|null} Informazioni sullo split rilevato
   */
  detectTrainingSplit(workouts) {
    // Implementazione base - può essere migliorata con algoritmi più avanzati
    
    // Estrai i gruppi muscolari allenati per ciascun allenamento
    const workoutMuscleGroups = this.extractWorkoutMuscleGroups(workouts);
    
    // Cerca pattern comuni
    const patterns = {
      pushPullLegs: 0,
      upperLower: 0,
      fullBody: 0,
      bodyPart: 0
    };
    
    // Analizza gruppi muscolari per ciascun allenamento
    workoutMuscleGroups.forEach(groups => {
      const uniqueGroups = [...new Set(groups)];
      
      // Verifica fullbody (più di 4 gruppi muscolari principali)
      if (uniqueGroups.length > 4) {
        patterns.fullBody++;
        return;
      }
      
      // Verifica pattern Push
      if (
        uniqueGroups.includes('Petto') && 
        uniqueGroups.includes('Spalle') && 
        uniqueGroups.includes('Tricipiti') &&
        uniqueGroups.length <= 4
      ) {
        patterns.pushPullLegs++;
        return;
      }
      
      // Verifica pattern Pull
      if (
        uniqueGroups.includes('Schiena') && 
        uniqueGroups.includes('Bicipiti') &&
        uniqueGroups.length <= 3
      ) {
        patterns.pushPullLegs++;
        return;
      }
      
      // Verifica pattern Legs
      if (
        uniqueGroups.includes('Gambe') &&
        uniqueGroups.length <= 2
      ) {
        patterns.pushPullLegs++;
        return;
      }
      
      // Verifica Upper
      if (
        (uniqueGroups.includes('Petto') || 
         uniqueGroups.includes('Schiena') || 
         uniqueGroups.includes('Spalle')) &&
        !uniqueGroups.includes('Gambe') &&
        uniqueGroups.length >= 2
      ) {
        patterns.upperLower++;
        return;
      }
      
      // Verifica Lower
      if (
        uniqueGroups.includes('Gambe') &&
        !uniqueGroups.includes('Petto') &&
        !uniqueGroups.includes('Schiena') &&
        !uniqueGroups.includes('Spalle')
      ) {
        patterns.upperLower++;
        return;
      }
      
      // Verifica bodypart (focus su un singolo gruppo)
      if (uniqueGroups.length === 1) {
        patterns.bodyPart++;
      }
    });
    
    // Determina il pattern più frequente
    const entries = Object.entries(patterns);
    entries.sort((a, b) => b[1] - a[1]);
    
    const [dominantPattern, count] = entries[0];
    
    // Calcola la percentuale di aderenza al pattern
    const totalWorkouts = workoutMuscleGroups.length;
    const adherencePercentage = (count / totalWorkouts) * 100;
    
    // Restituisci informazioni solo se il pattern è significativo
    if (adherencePercentage > 30) {
      return {
        type: 'split',
        pattern: dominantPattern,
        adherence: adherencePercentage.toFixed(1),
        confidence: this.calculatePatternConfidence(adherencePercentage)
      };
    }
    
    return null;
  }

  /**
   * Estrae i gruppi muscolari per ciascun allenamento
   * @param {Array} workouts - Allenamenti storici
   * @returns {Array} Array di array di gruppi muscolari per allenamento
   */
  extractWorkoutMuscleGroups(workouts) {
    const { sets } = this.userData;
    
    return workouts.map(workout => {
      // Trova tutte le serie associate a questo allenamento
      const workoutSets = sets.filter(set => 
        set.allenamento_id == workout.id || 
        set.workout_id == workout.id
      );
      
      // Estrai i gruppi muscolari
      const muscleGroups = workoutSets.map(set => set.gruppo_muscolare).filter(Boolean);
      
      return muscleGroups;
    });
  }

  /**
   * Rileva pattern di volume negli allenamenti
   * @param {Array} workouts - Allenamenti storici
   * @returns {Object|null} Informazioni sul pattern di volume
   */
  detectVolumePattern(workouts) {
    // Implementazione semplificata
    // In una versione completa, analizzerebbe il volume per sessione nel tempo
    return null;
  }

  /**
   * Rileva pattern di frequenza negli allenamenti
   * @param {Array} workouts - Allenamenti storici
   * @returns {Object|null} Informazioni sul pattern di frequenza
   */
  detectFrequencyPattern(workouts) {
    if (workouts.length < 7) return null;
    
    try {
      // Ordina gli allenamenti per data
      const sortedWorkouts = [...workouts].sort((a, b) => 
        new Date(a.data_allenamento) - new Date(b.data_allenamento)
      );
      
      // Calcola giorni tra allenamenti
      const intervals = [];
      for (let i = 1; i < sortedWorkouts.length; i++) {
        const current = new Date(sortedWorkouts[i].data_allenamento);
        const previous = new Date(sortedWorkouts[i-1].data_allenamento);
        const diffDays = Math.round((current - previous) / (24 * 60 * 60 * 1000));
        
        if (diffDays > 0 && diffDays < 14) { // Filtro outlier
          intervals.push(diffDays);
        }
      }
      
      if (intervals.length < 5) return null;
      
      // Calcola media e deviazione standard
      const avgInterval = intervals.reduce((sum, int) => sum + int, 0) / intervals.length;
      const variance = intervals.reduce((sum, int) => sum + Math.pow(int - avgInterval, 2), 0) / intervals.length;
      const stdDev = Math.sqrt(variance);
      
      // Calcola tipo di pattern (regolare o irregolare)
      const isRegular = stdDev < 1;
      
      // Calcola frequenza settimanale
      const weeklyFrequency = 7 / avgInterval;
      
      return {
        type: 'frequency',
        pattern: isRegular ? 'regular' : 'irregular',
        weeklyFrequency: weeklyFrequency.toFixed(1),
        intervals: {
          average: avgInterval.toFixed(1),
          stdDev: stdDev.toFixed(1)
        },
        confidence: this.calculatePatternConfidence(isRegular ? 90 : 70)
      };
    } catch (error) {
      console.error('Errore analizzando pattern di frequenza:', error);
      return null;
    }
  }

  /**
   * Calcola il livello di confidenza di un pattern in base alla percentuale
   * @param {number} percentage - Percentuale di aderenza
   * @returns {string} Livello di confidenza (low, medium, high)
   */
  calculatePatternConfidence(percentage) {
    if (percentage >= 70) return 'high';
    if (percentage >= 40) return 'medium';
    return 'low';
  }

  /**
   * Calcola metriche dell'utente basate sui dati storici
   */
  calculateUserMetrics() {
    if (!this.hasMinimumData()) return;
    
    try {
      // Calcola velocità di recupero media
      this.userMetrics.recoverySpeed = this.estimateRecoverySpeed();
      
      // Calcola tolleranza al volume
      this.userMetrics.volumeTolerance = this.estimateVolumeTolerance();
      
      // Calcola tolleranza all'intensità
      this.userMetrics.intensityTolerance = this.estimateIntensityTolerance();
      
      // Calcola tasso di progressione
      this.userMetrics.progressionRate = this.estimateProgressionRate();
      
      // Calcola frequenza dei plateau
      this.userMetrics.plateauFrequency = this.estimatePlateauFrequency();
    } catch (error) {
      console.error('Errore nel calcolo delle metriche utente:', error);
    }
  }

  /**
   * Stima la velocità di recupero dell'utente (in giorni)
   * @returns {number|null} Velocità di recupero stimata
   */
  estimateRecoverySpeed() {
    // Implementazione semplificata - la versione reale sarebbe più complessa
    const { workouts, sets } = this.userData;
    
    if (workouts.length < 10) return null;
    
    // Raggruppa set per gruppo muscolare e data
    const muscleGroupsByDate = {};
    
    sets.forEach(set => {
      const date = new Date(set.timestamp || set.data_allenamento).toISOString().split('T')[0];
      const muscleGroup = set.gruppo_muscolare;
      
      if (!muscleGroup) return;
      
      if (!muscleGroupsByDate[muscleGroup]) {
        muscleGroupsByDate[muscleGroup] = [];
      }
      
      if (!muscleGroupsByDate[muscleGroup].includes(date)) {
        muscleGroupsByDate[muscleGroup].push(date);
      }
    });
    
    // Calcola giorni medi tra allenamenti dello stesso gruppo muscolare
    const recoveryPeriods = [];
    
    for (const [group, dates] of Object.entries(muscleGroupsByDate)) {
      if (dates.length < 2) continue;
      
      // Ordina le date
      dates.sort();
      
      // Calcola intervalli
      for (let i = 1; i < dates.length; i++) {
        const current = new Date(dates[i]);
        const previous = new Date(dates[i-1]);
        const diffDays = Math.round((current - previous) / (24 * 60 * 60 * 1000));
        
        // Considera solo intervalli ragionevoli (1-14 giorni)
        if (diffDays > 0 && diffDays <= 14) {
          recoveryPeriods.push(diffDays);
        }
      }
    }
    
    if (recoveryPeriods.length === 0) return null;
    
    // Calcola media dei periodi di recupero
    const avgRecoveryDays = recoveryPeriods.reduce((sum, days) => sum + days, 0) / recoveryPeriods.length;
    
    return parseFloat(avgRecoveryDays.toFixed(1));
  }

  /**
   * Stima la tolleranza al volume dell'utente (scala 1-10)
   * @returns {number|null} Tolleranza al volume stimata
   */
  estimateVolumeTolerance() {
    // Implementazione semplificata
    return null;
  }

  /**
   * Stima la tolleranza all'intensità dell'utente (scala 1-10)
   * @returns {number|null} Tolleranza all'intensità stimata
   */
  estimateIntensityTolerance() {
    // Implementazione semplificata
    return null;
  }

  /**
   * Stima il tasso di progressione medio dell'utente (% a settimana)
   * @returns {number|null} Tasso di progressione stimato
   */
  estimateProgressionRate() {
    // Implementazione semplificata
    return null;
  }

  /**
   * Stima la frequenza media dei plateau dell'utente (in settimane)
   * @returns {number|null} Frequenza dei plateau stimata
   */
  estimatePlateauFrequency() {
    // Implementazione semplificata
    return null;
  }

  /**
   * Addestra i modelli base di ML
   * @param {Object} processedData - Dati preprocessati
   */
  async trainBaseModels(processedData) {
    // Qui si addestrerebbero i modelli ML
    // In una implementazione reale, si userebbero librerie come TensorFlow.js
    
    // Placeholder per i modelli addestrati
    this.trainedModels = {
      recoveryPredictor: {},
      plateauPredictor: {},
      progressPredictor: {}
    };
  }

  /**
   * Predice il tempo di recupero necessario per un gruppo muscolare specifico
   * @param {string} muscleGroup - Gruppo muscolare
   * @param {Object} lastWorkout - Dati ultimo allenamento
   * @returns {Object} Predizione di recupero
   */
  predictRecoveryTime(muscleGroup, lastWorkout) {
    if (!this.hasMinimumData() || !this.userMetrics.recoverySpeed) {
      // Fallback a valori di default se non abbiamo dati ML
      return this.defaultRecoveryEstimation(muscleGroup, lastWorkout);
    }
    
    // Usa le metriche personalizzate dell'utente
    const baseRecoveryDays = this.userMetrics.recoverySpeed;
    
    // Modifica in base all'intensità dell'ultimo allenamento
    let intensityMultiplier = 1.0;
    
    if (lastWorkout) {
      // Calcola intensità dell'ultimo allenamento (semplificato)
      const intensity = this.calculateWorkoutIntensity(lastWorkout);
      
      // Aggiusta il moltiplicatore in base all'intensità
      if (intensity > 8) intensityMultiplier = 1.3;
      else if (intensity > 6) intensityMultiplier = 1.15;
      else if (intensity < 4) intensityMultiplier = 0.85;
    }
    
    // Applica modificatori specifici per gruppo muscolare
    const muscleGroupFactor = this.getMuscleGroupRecoveryFactor(muscleGroup);
    
    // Calcola stima personalizzata
    const estimatedDays = baseRecoveryDays * intensityMultiplier * muscleGroupFactor;
    
    return {
      muscleGroup,
      estimatedDays: parseFloat(estimatedDays.toFixed(1)),
      confidence: 'medium',
      readiness: this.calculateReadinessScore(muscleGroup, estimatedDays)
    };
  }

  /**
   * Fornisce una stima di recupero di default senza ML
   * @param {string} muscleGroup - Gruppo muscolare
   * @param {Object} lastWorkout - Dati ultimo allenamento
   * @returns {Object} Predizione di recupero di default
   */
  defaultRecoveryEstimation(muscleGroup, lastWorkout) {
    // Valori di default in base ai gruppi muscolari
    const defaultRecovery = {
      'Petto': 2.5,
      'Schiena': 2.5,
      'Gambe': 3.0,
      'Spalle': 2.0,
      'Bicipiti': 1.5,
      'Tricipiti': 1.5,
      'Addominali': 1.0
    };
    
    const recoveryDays = defaultRecovery[muscleGroup] || 2.0;
    
    return {
      muscleGroup,
      estimatedDays: recoveryDays,
      confidence: 'low',
      readiness: 'unknown'
    };
  }

  /**
   * Calcola l'intensità di un allenamento (scala 1-10)
   * @param {Object} workout - Dati allenamento
   * @returns {number} Intensità stimata
   */
  calculateWorkoutIntensity(workout) {
    // Implementazione semplificata
    return 5; // Valore medio di default
  }

  /**
   * Ottiene fattore di recupero specifico per gruppo muscolare
   * @param {string} muscleGroup - Gruppo muscolare
   * @returns {number} Fattore di recupero
   */
  getMuscleGroupRecoveryFactor(muscleGroup) {
    const factors = {
      'Petto': 1.0,
      'Schiena': 1.0,
      'Gambe': 1.2,  // Le gambe richiedono più tempo
      'Spalle': 0.9,
      'Bicipiti': 0.8,
      'Tricipiti': 0.8,
      'Addominali': 0.7
    };
    
    return factors[muscleGroup] || 1.0;
  }

  /**
   * Calcola punteggio di prontezza per un gruppo muscolare
   * @param {string} muscleGroup - Gruppo muscolare
   * @param {number} daysToRecover - Giorni stimati per recupero
   * @returns {string} Livello di prontezza (low, medium, high)
   */
  calculateReadinessScore(muscleGroup, daysToRecover) {
    // Implementazione semplificata
    return 'medium'; // Valore di default
  }

  /**
   * Predice il rischio di plateau per un esercizio
   * @param {Object} exerciseData - Dati esercizio
   * @returns {Object} Predizione di rischio plateau
   */
  predictPlateauRisk(exerciseData) {
    // Implementazione semplificata
    return {
      exerciseName: exerciseData.name,
      riskLevel: 'medium',
      confidence: 'low',
      suggestedAction: 'Variare il range di ripetizioni'
    };
  }

  /**
   * Predice il volume ottimale per la prossima sessione
   * @param {string} muscleGroup - Gruppo muscolare
   * @param {Object} recentWorkouts - Allenamenti recenti
   * @returns {Object} Volume ottimale consigliato
   */
  predictOptimalVolume(muscleGroup, recentWorkouts) {
    // Implementazione semplificata
    return {
      muscleGroup,
      recommendedSets: 12,
      confidence: 'low'
    };
  }

  /**
   * Suggerisce un modello di periodizzazione in base ai dati utente
   * @returns {Object} Suggerimento di periodizzazione
   */
  suggestPeriodizationModel() {
    // Se non abbiamo dati sufficienti, usa raccomandazioni generali
    if (!this.hasMinimumData()) {
      return this.getDefaultPeriodizationSuggestion();
    }
    
    // Considera i pattern di allenamento rilevati
    const patternTypes = this.workoutPatterns.map(p => p.type);
    
    // Considera le metriche dell'utente
    const { recoverySpeed, volumeTolerance, intensityTolerance } = this.userMetrics;
    
    let recommendedModel = 'linear';
    let confidence = 'low';
    
    // Logica per determinare il modello più adatto
    if (patternTypes.includes('split') && recoverySpeed !== null) {
      if (recoverySpeed <= 1.5) {
        // Recupero veloce, può gestire alta frequenza
        recommendedModel = 'undulating';
        confidence = 'medium';
      } else if (recoverySpeed >= 3) {
        // Recupero lento, meglio blocchi più lunghi
        recommendedModel = 'block';
        confidence = 'medium';
      }
    }
    
    return {
      recommendedModel,
      confidence,
      explanation: this.getPeriodizationExplanation(recommendedModel),
      implementation: this.getPeriodizationImplementation(recommendedModel)
    };
  }

  /**
   * Fornisce un suggerimento di periodizzazione di default
   * @returns {Object} Suggerimento di periodizzazione di default
   */
  getDefaultPeriodizationSuggestion() {
    // La periodizzazione lineare è generalmente un buon punto di partenza
    return {
      recommendedModel: 'linear',
      confidence: 'low',
      explanation: this.getPeriodizationExplanation('linear'),
      implementation: this.getPeriodizationImplementation('linear')
    };
  }

  /**
   * Fornisce spiegazione per un modello di periodizzazione
   * @param {string} model - Modello di periodizzazione
   * @returns {string} Spiegazione del modello
   */
  getPeriodizationExplanation(model) {
    const explanations = {
      'linear': 'La periodizzazione lineare aumenta gradualmente l\'intensità e riduce il volume nel tempo, ideale per principianti e intermedi.',
      'undulating': 'La periodizzazione ondulata alterna giorni di alto volume/bassa intensità con giorni di basso volume/alta intensità, ottimale per atleti che recuperano velocemente.',
      'block': 'La periodizzazione a blocchi si concentra su obiettivi specifici per periodi estesi, perfetta per chi necessita di più tempo per recuperare.',
      'reverse': 'La periodizzazione inversa inizia con alta intensità e aumenta il volume nel tempo, utile per atleti avanzati in fase di preparazione.'
    };
    
    return explanations[model] || 'Modello di periodizzazione personalizzato basato sui tuoi dati di allenamento.';
  }

  /**
   * Fornisce dettagli implementativi per un modello di periodizzazione
   * @param {string} model - Modello di periodizzazione
   * @returns {Array} Passi di implementazione
   */
  getPeriodizationImplementation(model) {
    const implementations = {
      'linear': [
        'Settimane 1-3: Alto volume (4 serie x 12-15 rep), bassa intensità (60-70% 1RM)',
        'Settimane 4-6: Volume moderato (4 serie x 8-10 rep), intensità moderata (70-80% 1RM)',
        'Settimane 7-9: Basso volume (3-4 serie x 4-6 rep), alta intensità (80-90% 1RM)',
        'Settimana 10: Deload (2-3 serie x 8-10 rep), intensità moderata (60-70% 1RM)'
      ],
      'undulating': [
        'Giorno 1: Alto volume (4 serie x 12-15 rep), bassa intensità (60-70% 1RM)',
        'Giorno 2: Volume moderato (3-4 serie x 8-10 rep), intensità moderata (70-80% 1RM)',
        'Giorno 3: Basso volume (2-3 serie x 4-6 rep), alta intensità (80-90% 1RM)',
        'Ripeti il ciclo, inserendo una settimana di scarico ogni 4-6 settimane'
      ],
      'block': [
        'Blocco 1 (4 settimane): Ipertrofia - 4 serie x 8-12 rep, 70-75% 1RM',
        'Blocco 2 (4 settimane): Forza - 4-5 serie x 4-6 rep, 80-85% 1RM',
        'Blocco 3 (2 settimane): Potenza - 3 serie x 2-3 rep, 85-95% 1RM',
        'Blocco 4 (1 settimana): Deload - 2 serie x 8-10 rep, 60% 1RM'
      ],
      'reverse': [
        'Settimane 1-3: Basso volume (3 serie x 3-5 rep), alta intensità (85-90% 1RM)',
        'Settimane 4-6: Volume moderato (3-4 serie x 6-8 rep), intensità moderata (75-85% 1RM)',
        'Settimane 7-9: Alto volume (4-5 serie x 10-12 rep), intensità moderata (65-75% 1RM)',
        'Settimana 10: Deload (2 serie x 6-8 rep), intensità bassa (60% 1RM)'
      ]
    };
    
    return implementations[model] || [
      'Personalizza la tua periodizzazione in base ai tuoi dati di recupero e progressione',
      'Alterna periodi di volume e intensità basati sulla tua risposta individuale',
      'Inserisci settimane di scarico ogni 4-6 settimane per ottimizzare il recupero'
    ];
  }

  /**
   * Genera suggerimenti personalizzati in base ai dati ML
   * @returns {Array} Suggerimenti personalizzati
   */
  generatePersonalizedSuggestions() {
    const suggestions = [];
    
    // Aggiunge suggerimento per la periodizzazione
    const periodizationSuggestion = this.suggestPeriodizationModel();
    if (periodizationSuggestion) {
      suggestions.push({
        type: 'periodization_ml',
        title: `Periodizzazione ${this.getModelNameInItalian(periodizationSuggestion.recommendedModel)} consigliata`,
        description: periodizationSuggestion.explanation,
        icon: 'Activity',
        priority: 'medium',
        actionable: true,
        suggestedAction: `Implementa un modello di periodizzazione ${this.getModelNameInItalian(periodizationSuggestion.recommendedModel)}`,
        recommendations: periodizationSuggestion.implementation,
        data: {
          model: periodizationSuggestion.recommendedModel,
          confidence: periodizationSuggestion.confidence
        }
      });
    }
    
    // Aggiunge suggerimenti sul recupero
    if (this.userMetrics.recoverySpeed) {
      // Identifica gruppi muscolari che potrebbero aver bisogno di più recupero
      const overworkedMuscleGroups = this.identifyOverworkedMuscleGroups();
      
      if (overworkedMuscleGroups.length > 0) {
        suggestions.push({
          type: 'recovery_ml',
          title: 'Recupero ottimizzato',
          description: `In base alla tua storia di allenamento, hai bisogno di circa ${this.userMetrics.recoverySpeed} giorni per recuperare completamente.`,
          icon: 'Battery',
          priority: 'high',
          actionable: true,
          suggestedAction: `Aumenta il tempo di recupero per ${overworkedMuscleGroups[0]}`,
          data: {
            recoveryDays: this.userMetrics.recoverySpeed,
            overworkedGroups: overworkedMuscleGroups
          }
        });
      }
    }
    
    // Aggiunge suggerimenti sui pattern di allenamento
    if (this.workoutPatterns.length > 0) {
      const dominantPattern = this.workoutPatterns.find(p => p.confidence === 'high') || 
                             this.workoutPatterns[0];
      
      if (dominantPattern.type === 'split') {
        suggestions.push({
          type: 'pattern_ml',
          title: 'Pattern di allenamento rilevato',
          description: `Stai seguendo un pattern di tipo ${this.getSplitNameInItalian(dominantPattern.pattern)} con aderenza del ${dominantPattern.adherence}%.`,
          icon: 'Calendar',
          priority: 'low',
          actionable: false,
          data: {
            patternType: dominantPattern.pattern,
            adherence: dominantPattern.adherence,
            confidence: dominantPattern.confidence
          }
        });
      } else if (dominantPattern.type === 'frequency') {
        suggestions.push({
          type: 'pattern_ml',
          title: 'Frequenza di allenamento',
          description: `Ti alleni con una frequenza di ${dominantPattern.weeklyFrequency} volte a settimana con un pattern ${dominantPattern.pattern === 'regular' ? 'regolare' : 'irregolare'}.`,
          icon: 'Clock',
          priority: 'low',
          actionable: dominantPattern.pattern === 'irregular',
          suggestedAction: dominantPattern.pattern === 'irregular' ? 'Crea una routine di allenamento più consistente' : null,
          data: {
            frequency: dominantPattern.weeklyFrequency,
            pattern: dominantPattern.pattern,
            intervals: dominantPattern.intervals
          }
        });
      }
    }
    
    // Suggerimenti sulla progressione
    if (this.userMetrics.progressionRate) {
      suggestions.push({
        type: 'progression_ml',
        title: 'Tasso di progressione personalizzato',
        description: `In base ai tuoi dati, progredisci a un ritmo medio del ${this.userMetrics.progressionRate}% a settimana.`,
        icon: 'TrendingUp',
        priority: 'medium',
        actionable: true,
        suggestedAction: `Mira a incrementi settimanali del ${Math.round(this.userMetrics.progressionRate)}% per una progressione ottimale`,
        data: {
          rate: this.userMetrics.progressionRate
        }
      });
    }
    
    return suggestions;
  }
  
  /**
   * Identifica gruppi muscolari potenzialmente sovrallenati
   * @returns {Array} Gruppi muscolari sovrallenati
   */
  identifyOverworkedMuscleGroups() {
    // Implementazione semplificata
    // In una versione completa, analizzerebbe i pattern di recupero e volume per ogni gruppo
    return [];
  }
  
  /**
   * Converte il nome del modello di periodizzazione in italiano
   * @param {string} model - Nome del modello in inglese
   * @returns {string} Nome del modello in italiano
   */
  getModelNameInItalian(model) {
    const translations = {
      'linear': 'lineare',
      'undulating': 'ondulata',
      'block': 'a blocchi',
      'reverse': 'inversa'
    };
    
    return translations[model] || model;
  }
  
  /**
   * Converte il nome dello split in italiano
   * @param {string} split - Nome dello split in inglese
   * @returns {string} Nome dello split in italiano
   */
  getSplitNameInItalian(split) {
    const translations = {
      'pushPullLegs': 'Push/Pull/Legs',
      'upperLower': 'Upper/Lower',
      'fullBody': 'Full Body',
      'bodyPart': 'Body Part'
    };
    
    return translations[split] || split;
  }
}